/**
 * AI Personas Store (Zustand)
 *
 * Manages AI persona definitions with persistence via localStorage.
 * Admin users can edit personas (name, prompt, modules) through the
 * AI Config page. The store ships with default personas that can be
 * restored at any time.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { DEFAULT_PERSONAS, type AIPersona } from '@/lib/services/ai-personas'

interface AIPersonasState {
  personas: AIPersona[]
  addPersona: (persona: Omit<AIPersona, 'id'>) => void
  updatePersona: (id: string, updates: Partial<Omit<AIPersona, 'id'>>) => void
  deletePersona: (id: string) => void
  resetToDefaults: () => void
}

export const useAIPersonasStore = create<AIPersonasState>()(
  persist(
    (set) => ({
      personas: DEFAULT_PERSONAS,

      addPersona: (persona) =>
        set((state) => ({
          personas: [
            ...state.personas,
            { ...persona, id: crypto.randomUUID() },
          ],
        })),

      updatePersona: (id, updates) =>
        set((state) => ({
          personas: state.personas.map((p) =>
            p.id === id ? { ...p, ...updates } : p
          ),
        })),

      deletePersona: (id) =>
        set((state) => ({
          personas: state.personas.filter((p) => p.id !== id),
        })),

      resetToDefaults: () => set({ personas: DEFAULT_PERSONAS }),
    }),
    {
      name: 'ai-personas',
    }
  )
)
